import { Injectable, inject } from '@angular/core';

import { PlaceTerm } from '@bpos/v1/my-bets';
import { DisplayType, Fixture, OptionMarket, Participant, ParticipantMarketType, ParticipantPriceType, PlaceTerms } from '@cds/betting-offer';
import { EventModel, EventOptionGroup } from '@frontend/sports/betting-offer/feature/model';
import { Sitecore } from '@frontend/sports/common/client-config-data-access';
import { WinGameFixture } from '@frontend/sports/racing/feature/models';

import { BetslipPick } from '../../betslip/core/picks/betslip-pick';
import { BetslipV2OptionMarketPick } from '../../betslip/core/picks/betslip-v2-option-market-pick';

@Injectable({
    providedIn: 'root',
})
export class EachWayMarketHelperService {
    sitecore = inject(Sitecore);

    calculateEachWayTerms = (term: PlaceTerms | PlaceTerm) =>
        'fraction' in term
            ? this.calculateEachWayTermText(term.numberOfFirstPlaces, term.fraction.numerator, term.fraction.denominator)
            : this.calculateEachWayTermText(term.places, term.numerator, term.denominator);

    getWinMarketName(optionGroup: EventOptionGroup, event: EventModel): string | undefined {
        if (optionGroup.isEachWay && optionGroup.placeTerms) {
            const winMarketName = this.sitecore.event.Win;
            const placeTerms = this.calculateEachWayTerms(optionGroup.placeTerms);

            return `${winMarketName} & EW ${placeTerms}`;
        }

        if (optionGroup.linkedSibling && event.winGameLinkedFixture) {
            const winMarketName = this.getParticipantWinMarketName(event.winGameLinkedFixture);

            return winMarketName ?? '';
        }

        return undefined;
    }

    getWinMarketNameFromPick(pick: BetslipPick): string | undefined {
        if (pick instanceof BetslipV2OptionMarketPick && pick.isEachWay && pick.market.placeTerms) {
            const winMarketName = this.sitecore.event.Win;
            const placeTerms = this.calculateEachWayTerms(pick.market.placeTerms);

            return `${winMarketName} & EW ${placeTerms}`;
        }

        return undefined;
    }

    getParticipantWinMarketName(fixture: WinGameFixture | Fixture, nextRaces: boolean = false, isOptionBet?: boolean): string | null {
        const market = isOptionBet ? this.getOptionMarketWinOptions(fixture.optionMarkets)[0] : this.findWinOptions(fixture.participants)[0] || null;

        if (!market) {
            return null;
        }

        const marketName = this.sitecore.horseRacing.markets.WinShort;
        if (nextRaces) {
            return market.isEachWay ? `EW ${this.calculateEachWayTerms(market.placeTerms)}` : `${marketName}`;
        }

        return market.isEachWay ? `${marketName} & EW (${this.calculateEachWayTerms(market.placeTerms)})` : `${marketName}`;
    }

    findWinOptions(participants: Participant[]): { isEachWay: boolean; placeTerms: PlaceTerms }[] {
        return participants
            .flatMap((p) => p.options)
            .filter((o) => o.marketType === ParticipantMarketType.Win && o.priceType === ParticipantPriceType.Fixed)
            .map((m) => ({
                isEachWay: m.properties.isEachWay || false,
                placeTerms: m.properties.placeTerms,
            }));
    }

    getOptionMarketWinOptions(optionMarkets: OptionMarket[]): { isEachWay: boolean; placeTerms: PlaceTerms }[] {
        return optionMarkets
            .filter((o) => o.grouping.detailed[0].displayType === DisplayType.Wincast)
            .map((m) => ({
                isEachWay: m.isEachWay || false,
                placeTerms: m.placeTerms,
            }));
    }

    private calculateEachWayTermText(places: number, numerator: number, denominator: number): string {
        const placesText = [...Array(places)].map((_, i) => i + 1).join('-');

        return `${numerator}/${denominator} ${placesText}`;
    }
}
